import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { BoxProvider } from './context/BoxContext';
import { AuthProvider } from './context/authContext';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import BoxModal from './components/BoxModal';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import AnnouncementTicker from './components/AnnouncementTicker';

// Public pages
import Home from './pages/Home';
import Products from './pages/Products';
import Promotions from './pages/Promotions';
import ProductDetail from './pages/ProductDetail';
import PrivacyPolicy from './pages/Privacy';
import TermsOfService from './pages/Terms';
import FAQ from './pages/FAQ';
import BlogIndex from './pages/BlogIndex';
import BlogPost from './pages/BlogPost';

// Admin pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProduct from './pages/admin/AdminProduct';
import AdminPromotion from './pages/admin/AdminPromotion';
import AdminOrders from './pages/admin/AdminOrders';
import AdminBlogList from './pages/admin/AdminBlogList';
import AdminBlogForm from './pages/admin/AdminBlogForm';

// Services
import { emailService } from './services/emailService';

// Styles
import './App.css';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';

function App() {

  console.log('🌐 Using Firebase backend');

  useEffect(() => {
    // Initialize EmailJS on app start
    emailService.init();
  }, []);

  return (
    <AuthProvider>
      <BoxProvider>
        <Router>
          <div className="App">
            {/* Database Initializer - only show in development */}
            {/* {process.env.NODE_ENV === 'development' && <DatabaseInitializer />}*/}
            
            <ScrollToTop />
            <AnnouncementTicker />
            <Header />
            <main className="main-content">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/promotions" element={<Promotions />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/promotions/:id" element={<ProductDetail />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />

                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Product management */}
                <Route
                  path="/admin/products"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminProduct />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/products/add"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminProduct />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/products/:id/edit"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminProduct />
                    </ProtectedRoute>
                  }
                />

                {/* Promotion management */}
                <Route
                  path="/admin/promotions"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminPromotion />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/promotions/add"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminPromotion />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/promotions/:id/edit"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminPromotion />
                    </ProtectedRoute>
                  }
                />

                {/* Order management */}
                <Route
                  path="/admin/orders"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminOrders />
                    </ProtectedRoute>
                  }
                />

                {/* Blog management */}
                <Route
                  path="/admin/blog"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminBlogList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blog/add"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminBlogForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blog/:id/edit"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminBlogForm />
                    </ProtectedRoute>
                  }
                />

                {/* 404 Fallback */}
                <Route
                  path="*"
                  element={
                    <div className="not-found">
                      <div className="container">
                        <div className="not-found-content">
                          <i className="fas fa-exclamation-triangle"></i>
                          <h1>404 - Page Not Found</h1>
                          <p>The page you're looking for doesn't exist.</p>
                          <Link to="/" className="btn btn-primary">
                            <i className="fas fa-home"></i> Go Home
                          </Link>
                        </div>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </main>
            <Footer />

            {/* Box Modal */}
            <BoxModal />

            {/* Toast Notifications */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              closeOnClick
              draggable
              pauseOnHover
              theme="light"
            />
            <WhatsAppFloatingButton />
          </div>
        </Router>
      </BoxProvider>
    </AuthProvider>
  );
}

export default App;